/**
 * A photograph made small enough to send before it is sent, as the phone's image
 * picker does with `maxWidth`, `maxHeight` and `imageQuality`: a screenshot is never
 * shown larger than a bubble, and a village connection is what has to carry it.
 *
 * Redrawn as a JPEG no larger than [max] on its longer side. A file the browser
 * cannot draw — a HEIC in a browser that does not read them — goes as it came, and
 * the server says whether it will take it.
 */
export async function shrink(file: File, max: number, quality = 0.85): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((done) => canvas.toBlob(done, 'image/jpeg', quality));
    if (!blob) return file;
    const name = file.name.replace(/\.[^.]*$/, '') || 'photo';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/**
 * Opens the handset's own picker for one or more photographs — the gallery, or with
 * [camera] the camera itself, the one facing the passenger or away, where the browser has one to offer — and hands back
 * what was chosen. Nothing, when the picker was closed.
 */
export function pickPhotos(
  options: { camera?: 'user' | 'environment'; multiple?: boolean } = {},
): Promise<File[]> {
  return new Promise((done) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = options.multiple ?? false;
    if (options.camera) input.setAttribute('capture', options.camera);
    input.addEventListener('change', () => done(Array.from(input.files ?? [])));
    input.addEventListener('cancel', () => done([]));
    input.click();
  });
}
